import { useState } from "react";
import { useProfileDraft } from "@/context/ProfileDraftContext";
import { Button } from "@/components/ui/button";
import {
  FileText,
  Palette,
  CheckCircle2,
  Circle,
  ArrowRight,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Globe,
} from "lucide-react";
import type { SectionType } from "@profolio/validators";

export function HomeStudio() {
  const {
    draft,
    setActiveTab,
    onNavigateToSection,
    publishedUrl,
    displayDomain,
    hasUnsavedChanges,
    hasUnpublishedChanges,
  } = useProfileDraft();

  const [showCompleted, setShowCompleted] = useState(false);

  const profile = draft?.profile;
  const sections = draft?.sections || [];

  // Health checklist calculations with type-safe field inspection
  const hasBio = Boolean(profile?.bio && profile.bio.trim().length > 20);
  const hasPhoto = Boolean(profile?.photoUrl && profile.photoUrl.trim().length > 0);

  const publicationsSec = sections.find((s) => s.type === "publications");
  const hasPublications = Boolean(
    publicationsSec &&
    publicationsSec.content &&
    typeof publicationsSec.content === "object" &&
    Array.isArray((publicationsSec.content as Record<string, unknown>).publications) &&
    ((publicationsSec.content as Record<string, unknown>).publications as unknown[]).length > 0
  );

  const experienceSec = sections.find((s) => s.type === "experience");
  const hasExperience = Boolean(
    experienceSec &&
    experienceSec.content &&
    typeof experienceSec.content === "object" &&
    Array.isArray((experienceSec.content as Record<string, unknown>).experiences) &&
    ((experienceSec.content as Record<string, unknown>).experiences as unknown[]).length > 0
  );

  const contactSec = sections.find((s) => s.type === "contact");
  const hasContact = Boolean(
    contactSec &&
    contactSec.content &&
    typeof contactSec.content === "object" &&
    typeof (contactSec.content as Record<string, unknown>).email === "string" &&
    ((contactSec.content as Record<string, unknown>).email as string).trim().length > 0
  );

  const checklistItems = [
    { label: "Add academic bio & research title", done: hasBio, section: "about" as SectionType },
    { label: "Upload high-resolution portrait photo", done: hasPhoto, section: "about" as SectionType },
    { label: "Add or import research publications", done: hasPublications, section: "publications" as SectionType },
    { label: "Add academic faculty roles & appointments", done: hasExperience, section: "experience" as SectionType },
    { label: "Configure official email & office location", done: hasContact, section: "contact" as SectionType },
  ];

  const completedItems = checklistItems.filter((i) => i.done);
  const incompleteItems = checklistItems.filter((i) => !i.done);
  const completedCount = completedItems.length;
  const progressPercent = Math.round((completedCount / checklistItems.length) * 100);
  const hasPendingEdits = Boolean(hasUnsavedChanges || hasUnpublishedChanges);

  // SVG Circular progress constants
  const radius = 32;
  const circumference = 2 * Math.PI * radius;
  const strokeOffset = circumference - (progressPercent / 100) * circumference;

  return (
    <div className="max-w-4xl mx-auto p-6 md:p-8 space-y-6">
      {/* ── Streamlined Welcome & Overview Card ── */}
      <div className="rounded-2xl border border-border/80 bg-card p-6 md:p-7 shadow-xs">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div className="flex items-center gap-5">
            {/* Circular Progress Ring */}
            <div className="relative w-20 h-20 shrink-0 flex items-center justify-center">
              <svg className="w-full h-full -rotate-90" viewBox="0 0 80 80">
                <circle
                  cx="40"
                  cy="40"
                  r={radius}
                  className="stroke-muted"
                  strokeWidth="6"
                  fill="none"
                />
                <circle
                  cx="40"
                  cy="40"
                  r={radius}
                  className="stroke-primary transition-all duration-700 ease-out"
                  strokeWidth="6"
                  strokeDasharray={circumference}
                  strokeDashoffset={strokeOffset}
                  strokeLinecap="round"
                  fill="none"
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-sm font-extrabold text-foreground">{progressPercent}%</span>
              </div>
            </div>

            {/* Profile Greeting & Subtitle */}
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-bold tracking-tight text-foreground">
                  {profile?.fullName ? `Welcome back, ${profile.fullName}` : "Academic Overview"}
                </h1>
              </div>
              <p className="text-xs text-muted-foreground">
                {progressPercent === 100
                  ? "All portfolio milestones completed. Your site is fully optimized."
                  : `${completedCount} of ${checklistItems.length} setup milestones completed.`}
              </p>

              {/* Status pills row */}
              <div className="flex items-center gap-2 pt-1 flex-wrap">
                <span
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                    hasPendingEdits
                      ? "bg-amber-500/10 text-amber-500 border border-amber-500/20"
                      : "bg-emerald-500/10 text-emerald-500 border border-emerald-500/20"
                  }`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${hasPendingEdits ? "bg-amber-500" : "bg-emerald-500"}`} />
                  {hasPendingEdits ? "Unpublished Changes" : "Up to Date"}
                </span>

                {profile?.subdomain && (
                  <a
                    href={publishedUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono text-muted-foreground hover:text-foreground bg-muted hover:bg-muted/80 transition-colors border border-border/60"
                  >
                    <Globe className="h-2.5 w-2.5" />
                    <span>{displayDomain}</span>
                    <ExternalLink className="h-2.5 w-2.5 ml-0.5" />
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Primary Quick Navigation */}
          <div className="flex items-center gap-2 w-full sm:w-auto shrink-0">
            <Button
              size="sm"
              onClick={() => setActiveTab("content")}
              className="text-xs h-9 gap-1.5 flex-1 sm:flex-initial font-medium shadow-xs"
            >
              <FileText className="h-3.5 w-3.5" />
              <span>Edit Content</span>
              <ArrowRight className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setActiveTab("design")}
              className="text-xs h-9 gap-1.5 flex-1 sm:flex-initial"
            >
              <Palette className="h-3.5 w-3.5" />
              <span>Design</span>
            </Button>
          </div>
        </div>
      </div>

      {/* ── Checklist: Progressive Disclosure ── */}
      <div className="rounded-2xl border border-border/80 bg-card p-6 shadow-xs space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-sm font-bold text-foreground">Profile Milestones</h2>
            <p className="text-xs text-muted-foreground">
              Essential items recommended for peer discovery and academic impact
            </p>
          </div>
          <span className="text-[11px] font-mono font-semibold px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
            {completedCount} / {checklistItems.length}
          </span>
        </div>

        {/* Incomplete items (always shown prominently) */}
        {incompleteItems.length > 0 ? (
          <div className="space-y-2">
            {incompleteItems.map((item, idx) => (
              <div
                key={idx}
                onClick={() => onNavigateToSection(item.section)}
                className="group flex items-center justify-between p-3 rounded-xl border border-border/80 bg-background hover:border-primary/40 hover:bg-muted/20 transition-all cursor-pointer shadow-2xs"
              >
                <div className="flex items-center gap-3">
                  <Circle className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
                  <span className="text-xs font-medium text-foreground">{item.label}</span>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground group-hover:text-primary transition-colors">
                  <span className="text-[11px] hidden sm:inline">Complete</span>
                  <ArrowRight className="h-3.5 w-3.5" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-4 text-center space-y-1">
            <CheckCircle2 className="h-6 w-6 text-emerald-500 mx-auto" />
            <p className="text-xs font-semibold text-foreground">Outstanding! All milestones complete.</p>
            <p className="text-[11px] text-muted-foreground">
              Your profile is thoroughly populated and ready for academic visitors.
            </p>
          </div>
        )}

        {/* Collapsible Completed Milestones */}
        {completedItems.length > 0 && incompleteItems.length > 0 && (
          <div className="pt-2">
            <button
              type="button"
              onClick={() => setShowCompleted(!showCompleted)}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground font-medium py-1 transition-colors"
            >
              {showCompleted ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
              <span>
                {showCompleted ? "Hide" : "Show"} completed milestones ({completedItems.length})
              </span>
            </button>

            {showCompleted && (
              <div className="space-y-1.5 pt-2">
                {completedItems.map((item, idx) => (
                  <div
                    key={idx}
                    onClick={() => onNavigateToSection(item.section)}
                    className="flex items-center justify-between p-2.5 rounded-lg border border-border/40 bg-muted/20 hover:bg-muted/40 transition-colors cursor-pointer text-xs"
                  >
                    <div className="flex items-center gap-2.5">
                      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                      <span className="text-muted-foreground line-through">{item.label}</span>
                    </div>
                    <ArrowRight className="h-3 w-3 text-muted-foreground" />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
